import { SEO_BASE_URL } from './seo/seo.config';

// Plausible is cookieless — no consent banner needed, unlike GA4 — and
// needs no DSN/API key, just this one public script tag. See
// docs/monitoring.md's "Full isolation + analytics" section: the box it's
// served from is provisioned separately and stays inert (this script 404s
// harmlessly) until that's done.
const PLAUSIBLE_DOMAIN = new URL(SEO_BASE_URL).hostname;
// Extended tracker build (file-downloads, hash, outbound-links,
// pageview-props, revenue, tagged-events) rather than the bare script.js —
// enables plausible('event', ...) calls for those extensions.
const PLAUSIBLE_SCRIPT_SRC = `https://analytics.${PLAUSIBLE_DOMAIN}/js/script.file-downloads.hash.outbound-links.pageview-props.revenue.tagged-events.js`;

interface PlausibleWindow extends Window {
  plausible?: { (...args: unknown[]): void; q?: unknown[] };
  gtag?: (...args: unknown[]) => void;
}

/**
 * Loads Plausible's page-view tracker. Called from main.ts, which never
 * runs during prerendering (main.server.ts is prerendering's separate
 * entry point and doesn't call this) — the `document` guard is cheap
 * insurance against page-view events firing for a build instead of a real
 * visit if that ever changes. Unlike GoogleAnalyticsService, this isn't
 * route-gated — it tracks every route it's loaded on, including
 * /dashboard (see isGaTrackedPath in google-analytics.service.ts for
 * GA4's equivalent gating, which excludes only /admin).
 */
export function initAnalytics(): void {
  if (typeof document === 'undefined') return;

  // Queue shim so plausible('event', ...) calls (tagged-events/revenue)
  // made before the deferred script loads aren't lost — Plausible's script
  // drains window.plausible.q once it's ready.
  const w = window as PlausibleWindow;
  w.plausible =
    w.plausible ||
    function (...args: unknown[]) {
      (w.plausible!.q = w.plausible!.q || []).push(args);
    };

  const script = document.createElement('script');
  script.defer = true;
  script.dataset['domain'] = PLAUSIBLE_DOMAIN;
  script.src = PLAUSIBLE_SCRIPT_SRC;
  document.head.appendChild(script);
}

/**
 * Fires a custom funnel/conversion event to both Plausible and GA4 in one
 * call, so components don't need to know about either SDK's calling
 * convention. Both are safe no-ops when their respective script hasn't
 * loaded yet (Plausible's queue shim above, GA4's optional chaining here —
 * gtag is undefined until GoogleAnalyticsService.enable() has run, which
 * requires cookie consent; see google-analytics.service.ts) or never loads
 * at all (GA_MEASUREMENT_ID unset, consent denied).
 *
 * Unlike page_view tracking, this is NOT route-gated to exclude
 * /dashboard — conversion events like checkout_started matter regardless
 * of which surface they happen on, whereas per-page dashboard browsing
 * volume isn't a marketing-funnel metric.
 */
export function trackEvent(name: string, props?: Record<string, string | number | boolean>): void {
  if (typeof document === 'undefined') return;
  const w = window as PlausibleWindow;
  w.plausible?.(name, props ? { props } : undefined);
  w.gtag?.('event', name, props);
}
